import React, { useMemo } from "react";
import { FormControl, IconButton, MenuItem, Select, TextField, Tooltip } from "@material-ui/core";
import { Lock, LockOpen } from "@material-ui/icons";
import { Controller, useFormContext } from "react-hook-form";
import { TemplateFormData, TemplateFormExercise } from "../types";
import { MAX_SETS } from "../constants";

type Props = {
  index: number;
  maxWeeks: number;
  readOnly?: boolean;
  // Field snapshot for this row. RHF v6 does not wire useFieldArray values into
  // nested Controllers on append, so each Controller is seeded via defaultValue
  // from here (otherwise appended exercises render blank inputs).
  defaults: TemplateFormExercise;
};

const range = (max: number): number[] => Array.from({ length: Math.max(1, max) }, (_, i) => i + 1);
const SETS_OPTIONS = range(MAX_SETS);

// Per-exercise controls: start/end week, level (+lock), sets, reps. Rendered
// with display:contents so each control is a direct grid item of the section's
// shared column template — the header row carries the labels.
const ExerciseRow: React.VFC<Props> = ({ index, maxWeeks, defaults, readOnly = false }) => {
  const { control } = useFormContext<TemplateFormData>();
  const base = `exercises.${index}` as const;

  const maxLevel = defaults.max_level ?? 1;
  const weeks = useMemo(() => range(maxWeeks), [maxWeeks]);
  const levels = useMemo(() => range(maxLevel), [maxLevel]);

  return (
    <div style={{ display: "contents" }}>
      <Controller
        control={control}
        name={`${base}.start_week` as const}
        defaultValue={defaults.start_week}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" disabled={readOnly}>
            <Select
              value={value}
              onChange={e => onChange(Number(e.target.value))}
              inputProps={{ "aria-label": "Start week" }}
            >
              {weeks.map(w => (
                <MenuItem key={w} value={w}>
                  Week {w}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.end_week` as const}
        defaultValue={defaults.end_week}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" disabled={readOnly}>
            <Select
              value={value == null ? "ongoing" : value}
              onChange={e => onChange(e.target.value === "ongoing" ? null : Number(e.target.value))}
              inputProps={{ "aria-label": "End week" }}
            >
              <MenuItem value="ongoing">Ongoing</MenuItem>
              {weeks.map(w => (
                <MenuItem key={w} value={w}>
                  Week {w}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.recommended_level` as const}
        defaultValue={defaults.recommended_level}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" disabled={readOnly}>
            <Select
              value={value}
              onChange={e => onChange(Number(e.target.value))}
              inputProps={{ "aria-label": "Level" }}
            >
              {levels.map(l => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.level_locked` as const}
        defaultValue={defaults.level_locked}
        render={({ onChange, value }) => (
          <Tooltip title={value ? "Level locked" : "Level unlocked"}>
            <span>
              <IconButton
                size="small"
                disabled={readOnly}
                aria-label={value ? "Unlock level" : "Lock level"}
                onClick={() => onChange(!value)}
              >
                {value ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      />

      <Controller
        control={control}
        name={`${base}.sets` as const}
        defaultValue={defaults.sets}
        render={({ onChange, value }) => (
          <FormControl variant="outlined" size="small" disabled={readOnly}>
            <Select
              value={value ?? ""}
              onChange={e => onChange(Number(e.target.value))}
              inputProps={{ "aria-label": "Sets" }}
            >
              {SETS_OPTIONS.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name={`${base}.reps` as const}
        defaultValue={defaults.reps}
        render={({ onChange, value }) => (
          <TextField
            variant="outlined"
            size="small"
            disabled={readOnly}
            inputProps={{ "aria-label": "Repetitions" }}
            value={value ?? ""}
            onChange={e => onChange(e.target.value)}
          />
        )}
      />
    </div>
  );
};

export default ExerciseRow;
